/**
 * AspectObservationContent — renders `aspect_observation` entries.
 *
 * The busiest entry type in the ledger and, before this remap, one of ten with no component at all
 * — every one fell through to GenericEventContent with an untranslated key.
 *
 * `payload` is the aspect's own property bag, so its shape varies per aspect. Rather than guess at
 * field names this renders the observed properties generically: correct for every aspect, instead
 * of right for one and wrong for the rest.
 */
import React from 'react';
import { useTranslation } from 'localization';
import { TimelineEvent } from 'types/timeline';

interface Props {
  event: TimelineEvent;
}

/**
 * Reducer bookkeeping (EAINT-754 Phase 6), not observed values. Rendering these as if a user
 * reported them would misrepresent the entry.
 */
const REDUCER_KEYS = new Set(['inputs', 'excluded', 'triggeredBy', 'method', 'sourceAspect']);

const AspectObservationContent: React.FC<Props> = ({ event }) => {
  const { t } = useTranslation();
  const payload = (event.payload ?? {}) as Record<string, unknown>;
  const aspect = (event as unknown as { aspectSystemName?: string }).aspectSystemName;
  const values = Object.entries(payload).filter(([k]) => !REDUCER_KEYS.has(k));

  return (
    <div className="timeline-event-content">
      {aspect ? <span className="timeline-event-content__label">{aspect}</span> : null}
      {values.map(([key, value], index) => (
        <span key={key} className="timeline-event-content__text">
          {/* Inline spans render with no whitespace between them, so the separator is content. */}
          {index > 0 ? ' · ' : ''}
          {t('timeline.eventContent.aspectValue', { property: key, value: String(value) })}
        </span>
      ))}
      {values.length === 0 ? (
        <span className="timeline-event-content__text">{t('timeline.eventContent.aspectObserved')}</span>
      ) : null}
    </div>
  );
};

export default AspectObservationContent;
